import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/state/store/auth';
import { useMyOrganizations } from '@/graphql/queries/organizations';
import { useSwitchOrg } from '@/api-client/use-swich-organization';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { orgPath } from '../utils/org-path';
import { SYSTEM_ADMIN_ORG_ID, type MyOrganization } from '@/graphql/queries/organizations';
import { useCreateOrganization } from '@/api-client/use-create-orginization';
import { fetchMyOrganizations } from '@/api-client/use-my-ognization';
import {
  setupOrganization,
  type OrganizationSetupStep,
} from '@/api-client/use-create-orginization/setup-organization';
import { useInsertOrganization } from '@/graphql/mutations/organizations';
import { useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '@/store';
import { getDisplayErrorMessage } from '@/lib/utils/api-error';
import { useToast } from '@/hooks/use-toast';
import { OrgCreationProgressDialog } from '../components/org-creation-progress-dialog';
import { LoadingOverlay } from '@/components/core';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui-kit/avatar';
import { Plus } from 'lucide-react';

const ROLE_LABELS: Record<string, string> = {
  org_admin: 'Admin',
  org_editor: 'Editor',
  org_member: 'Member',
  companyadmin: 'Company Admin',
};

const OrgSelectPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { data: dbOrgs, isLoading } = useMyOrganizations();
  const { mutateAsync: switchOrganizationAsync, isPending } = useSwitchOrg();
  const { handleError } = useErrorHandler();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const setupUser = useUserStore((state) => state.user);
  const { createOrganizationAsync } = useCreateOrganization();
  const { insertOrganizationAsync } = useInsertOrganization();
  const [progressOpen, setProgressOpen] = useState(false);
  const [steps, setSteps] = useState<OrganizationSetupStep[]>([]);
  const [provisionError, setProvisionError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const processingRef = useRef(false);

  const closeProgress = () => {
    if (processingRef.current) return;
    setProgressOpen(false);
    setSteps([]);
  };

  const createOrganization = async (
    name: string,
    signatureOrgId?: string,
    uploadLogo?: () => Promise<string | null>
  ) => {
    if (processingRef.current || isPending) return;
    if (!setupUser?.itemId) {
      setProvisionError('Please sign in before creating an organization.');
      setProgressOpen(true);
      return;
    }
    processingRef.current = true;
    setIsProcessing(true);
    setProvisionError(null);
    setSteps([]);
    setProgressOpen(true);
    try {
      const org = await setupOrganization(
        { name, signatureOrgId, uploadLogo, user: setupUser },
        {
          createOrganization: createOrganizationAsync,
          insertOrganization: insertOrganizationAsync,
          getOrganizations: fetchMyOrganizations,
          onProgress: setSteps,
        }
      );
      await switchOrganizationAsync({
        organization_id: org.BlocksOrgId,
        organization_name: name,
      });
      useAuthStore.getState().setOrg(org.BlocksOrgId, org.Slug, org.ItemId, org.SigOrgId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['me'] }),
        queryClient.invalidateQueries({ queryKey: ['my-ognization'] }),
        queryClient.invalidateQueries({ queryKey: ['my-uds-organizations'] }),
      ]);
      toast({ variant: 'success', title: t('COMMON_CREATEORGANIZATION') });
      navigate(orgPath(org.BlocksOrgId));
    } catch (error) {
      setProvisionError(getDisplayErrorMessage(error));
    } finally {
      processingRef.current = false;
      setIsProcessing(false);
    }
  };

  const getRoleForOrg = (blocksOrgId: string) => {
    const membership = user?.memberships?.find((m) => m.organizationId === blocksOrgId);
    const role = membership?.roles?.[0];
    return role ? (ROLE_LABELS[role] ?? role) : null;
  };

  const handleSelect = async (org: MyOrganization) => {
    if (isPending || isProcessing) return;
    try {
      await switchOrganizationAsync({
        organization_id: org.BlocksOrgId,
        organization_name: org.Name,
      });
      useAuthStore
        .getState()
        .setOrg(org.BlocksOrgId, org.Slug || org.BlocksOrgId, org.ItemId, org.SigOrgId);
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      navigate(org.BlocksOrgId === SYSTEM_ADMIN_ORG_ID ? '/admin' : orgPath(org.BlocksOrgId));
    } catch (error) {
      handleError(error, { defaultMessage: 'Failed to switch organization' });
    }
  };

  if (isLoading) {
    return <LoadingOverlay />;
  }

  const orgs = dbOrgs?.blocksOrgs ?? [];
  const sigOrgs = dbOrgs?.sigOrgsWithoutBlocks ?? [];

  return (
    <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center px-4 py-12">
      <OrgCreationProgressDialog
        open={progressOpen}
        steps={steps}
        error={provisionError}
        onClose={closeProgress}
      />
      <div className="w-full max-w-lg">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold text-foreground">
            {t('SELECT_ORGANIZATION', 'Select an Organization')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('SELECT_ORGANIZATION_DESCRIPTION', 'Choose an organization to continue')}
          </p>
        </div>

        <div className="space-y-2">
          {orgs.map((org) => (
            <button
              key={org.BlocksOrgId}
              type="button"
              onClick={() => handleSelect(org)}
              disabled={isPending || isProcessing}
              className="flex w-full items-center gap-4 rounded-lg border border-border bg-card p-4 text-left transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Avatar className="h-10 w-10 shrink-0">
                {org.LogoUrl && <AvatarImage src={org.LogoUrl} alt={org.Name} />}
                <AvatarFallback className="bg-selise-blue-500 text-white text-sm font-semibold">
                  {org.Name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <span className="block truncate font-medium text-foreground">{org.Name}</span>
                {getRoleForOrg(org.BlocksOrgId) && (
                  <span className="block text-xs text-muted-foreground">
                    {getRoleForOrg(org.BlocksOrgId)}
                  </span>
                )}
              </div>
            </button>
          ))}
          {sigOrgs.length > 0 && (
            <div className="pt-4 mb-3">
              <h2 className="px-2 text-xs font-medium text-globe-grey mb-3">
                Continue With SELISE Signature Organizations
              </h2>
              <div className="mt-2 space-y-2">
                {sigOrgs.map(
                  (org: { OrganizationId: string; OrganizationName: string; Role: string }) => (
                    <button
                      key={org.OrganizationId}
                      type="button"
                      disabled={isProcessing || isPending}
                      onClick={() =>
                        void createOrganization(org.OrganizationName, org.OrganizationId)
                      }
                      className="flex w-full items-center gap-4 rounded-lg border border-border bg-card p-4 text-left transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Avatar className="h-10 w-10 shrink-0">
                        <AvatarFallback className="bg-selise-blue-500 text-white text-sm font-semibold">
                          {org.OrganizationName.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <span className="block truncate font-medium text-foreground">
                          {org.OrganizationName}
                        </span>

                        <span className="block text-xs text-muted-foreground">
                          {ROLE_LABELS[org.Role] ?? org.Role}
                        </span>
                      </div>
                    </button>
                  )
                )}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => navigate('/org/new')}
            className="flex w-full items-center gap-4 rounded-lg border border-dashed border-border p-4 text-left transition-colors hover:bg-muted"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-dashed border-border">
              <Plus className="h-5 w-5 text-muted-foreground" />
            </div>
            <span className="font-medium text-muted-foreground">
              {t('COMMON_CREATEORGANIZATION', 'Create Organization')}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrgSelectPage;
